import { blobticService } from '../../Commun/services/blobticService';
import {
  PlanningTheorique,
  PlanningTheoriqueVac,
  PlanningGenerationParams,
  PlanningGenerationResult,
  PlanningDeletionFilters,
  SessionDeletionItem
} from '../types/planningTheorique';

const THEO_OKEY_PREFIX = 'Blobtic_Academy,academy_planning_theorique';
const VACS_OKEY_PREFIX = 'Blobtic_Academy,academy_planning_theorique_vacs';
const GEN_OKEY_PREFIX = 'Blobtic_Academy,academy_planning_generation';
const DEL_OKEY_PREFIX = 'Blobtic_Academy,academy_planning_deletion';

export const dtoToPlanningTheorique = (dto: any): PlanningTheorique => {
  const startDateRaw = dto.THEO_START_DATE || dto.start_date || '';
  const endDateRaw = dto.THEO_END_DATE || dto.end_date;

  const formatDateOnly = (val?: any): string => {
    if (!val) return '';
    if (typeof val === 'string') return val.split('T')[0];
    try {
      const d = new Date(val);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch {
      return String(val);
    }
  };

  return {
    id: Number(dto.THEO_IDF || dto.id || 0),
    uid: dto.THEO_UIDF || dto.uid,
    dos_idf: Number(dto.DOS_IDF || dto.dos_idf || 1),
    arm_idf: Number(dto.ARM_IDF || dto.arm_idf || 0),
    nbr_week: Number(dto.THEO_NBR_WEEK || dto.nbr_week || 1),
    start_date: formatDateOnly(startDateRaw),
    end_date: endDateRaw ? formatDateOnly(endDateRaw) : null,
    day_type: dto.THEO_DAY_TYPE || dto.day_type || 'Normal',
    room_number: dto.ARM_ROOM_NUMBER || dto.room_number,
    room_lib: dto.ROOM_LIB || dto.room_lib,
    vacs_count: Number(dto.VACS_COUNT || dto.vacs_count || 0),
    last_updt: dto.LAST_UPDT || dto.last_updt,
    user_idf: dto.USER_IDF != null ? Number(dto.USER_IDF) : undefined
  };
};

export const planningTheoriqueToDto = (item: Partial<PlanningTheorique>): Record<string, any> => {
  const dto: Record<string, any> = {};
  if (item.id != null && item.id > 0) dto.THEO_IDF = item.id;
  if (item.uid) dto.THEO_UIDF = item.uid;
  if (item.dos_idf != null) dto.DOS_IDF = item.dos_idf;
  if (item.arm_idf != null) dto.ARM_IDF = item.arm_idf;
  if (item.nbr_week != null) dto.THEO_NBR_WEEK = item.nbr_week;
  if (item.start_date) dto.THEO_START_DATE = item.start_date;
  if (item.end_date !== undefined) dto.THEO_END_DATE = item.end_date || null;
  if (item.day_type) dto.THEO_DAY_TYPE = item.day_type;
  return dto;
};

export const dtoToVac = (dto: any): PlanningTheoriqueVac => {
  const formatTimeOnly = (val: any): string => {
    if (!val) return '08:00';
    if (typeof val === 'string') {
      if (val.includes('T')) {
        const timePart = val.split('T')[1];
        return timePart.substring(0, 5);
      }
      if (val.includes(':')) {
        return val.substring(0, 5);
      }
    }
    try {
      const d = new Date(val);
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      return `${h}:${m}`;
    } catch {
      return '08:00';
    }
  };

  return {
    id: Number(dto.THEOVAC_IDF || dto.id || 0),
    uid: dto.THEOVAC_UIDF || dto.uid,
    theo_idf: Number(dto.THEO_IDF || dto.theo_idf || 0),
    dos_idf: Number(dto.DOS_IDF || dto.dos_idf || 1),
    arm_idf: Number(dto.ARM_IDF || dto.arm_idf || 0),
    grp_idf: dto.GRP_IDF != null ? Number(dto.GRP_IDF) : (dto.grp_idf ?? null),
    tch_idf: dto.TCH_IDF != null ? Number(dto.TCH_IDF) : (dto.tch_idf ?? null),
    start_time: formatTimeOnly(dto.THEOVAC_START_TIME || dto.start_time),
    end_time: formatTimeOnly(dto.THEOVAC_END_TIME || dto.end_time),
    day_num: Number(dto.THEOVAC_DAY_NUM || dto.day_num || 1),
    room_number: dto.ARM_ROOM_NUMBER || dto.room_number,
    room_lib: dto.ROOM_LIB || dto.room_lib,
    grp_name: dto.GRP_NAME || dto.grp_name,
    grp_code: dto.GRP_CODE || dto.grp_code,
    teacher_name: dto.TEACHER_NAME || dto.teacher_name,
    last_updt: dto.LAST_UPDT || dto.last_updt,
    user_idf: dto.USER_IDF != null ? Number(dto.USER_IDF) : undefined
  };
};

export const vacToDto = (item: Partial<PlanningTheoriqueVac>): Record<string, any> => {
  const dto: Record<string, any> = {};
  if (item.id != null && item.id > 0) dto.THEOVAC_IDF = item.id;
  if (item.uid) dto.THEOVAC_UIDF = item.uid;
  if (item.theo_idf != null) dto.THEO_IDF = item.theo_idf;
  if (item.dos_idf != null) dto.DOS_IDF = item.dos_idf;
  if (item.arm_idf != null) dto.ARM_IDF = item.arm_idf;
  if (item.grp_idf !== undefined) dto.GRP_IDF = item.grp_idf || null;
  if (item.tch_idf !== undefined) dto.TCH_IDF = item.tch_idf || null;
  if (item.day_num != null) dto.THEOVAC_DAY_NUM = item.day_num;

  // Base date 2001-01-01 for start and end time
  if (item.start_time) {
    dto.THEOVAC_START_TIME = `2001-01-01T${item.start_time}:00`;
  }
  if (item.end_time) {
    dto.THEOVAC_END_TIME = `2001-01-01T${item.end_time}:00`;
  }

  return dto;
};

export const planningTheoriqueApi = {
  // ── Plannings Théoriques CRUD ──
  async getPlanningTheoriques(roomId?: number, useCache: boolean = false): Promise<PlanningTheorique[]> {
    const action: Record<string, any> = {
      OKey: `${THEO_OKEY_PREFIX},select`
    };
    if (roomId && roomId > 0) action.ARM_IDF = roomId;

    const res = await blobticService.executeZoomRequest<any[]>(
      {
        ZoomKey: 'zoom_planning_theorique',
        Action: action as any,
        useCache
      },
      useCache,
      false
    );

    if (res.success && Array.isArray(res.data)) {
      return res.data.map(dtoToPlanningTheorique);
    }
    return [];
  },

  async insertPlanningTheorique(item: Partial<PlanningTheorique>): Promise<any> {
    const dto = planningTheoriqueToDto(item);
    const res = await blobticService.executeZoomRequest<any>(
      {
        ZoomKey: 'zoom_planning_theorique',
        Action: {
          OKey: `${THEO_OKEY_PREFIX},insert`,
          ...dto
        },
        useCache: false
      },
      false,
      true
    );
    return res;
  },

  async updatePlanningTheorique(item: Partial<PlanningTheorique>): Promise<any> {
    const dto = planningTheoriqueToDto(item);
    const res = await blobticService.executeZoomRequest<any>(
      {
        ZoomKey: 'zoom_planning_theorique',
        Action: {
          OKey: `${THEO_OKEY_PREFIX},update`,
          ...dto
        },
        useCache: false
      },
      false,
      true
    );
    return res;
  },

  async deletePlanningTheorique(id: number): Promise<any> {
    const res = await blobticService.executeZoomRequest<any>(
      {
        ZoomKey: 'zoom_planning_theorique',
        Action: {
          OKey: `${THEO_OKEY_PREFIX},delete`,
          THEO_IDF: id
        },
        useCache: false
      },
      false,
      true
    );
    return res;
  },

  // ── Créneaux Théoriques (Vacs) CRUD ──
  async getPlanningTheoriqueVacs(theoId?: number, roomId?: number, useCache: boolean = false): Promise<PlanningTheoriqueVac[]> {
    const action: Record<string, any> = {
      OKey: `${VACS_OKEY_PREFIX},select`
    };
    if (theoId && theoId > 0) action.THEO_IDF = theoId;
    if (roomId && roomId > 0) action.ARM_IDF = roomId;

    const res = await blobticService.executeZoomRequest<any[]>(
      {
        ZoomKey: 'zoom_planning_theorique_vacs',
        Action: action as any,
        useCache
      },
      useCache,
      false
    );

    if (res.success && Array.isArray(res.data)) {
      return res.data.map(dtoToVac);
    }
    return [];
  },

  async insertPlanningTheoriqueVac(item: Partial<PlanningTheoriqueVac>): Promise<any> {
    const dto = vacToDto(item);
    const res = await blobticService.executeZoomRequest<any>(
      {
        ZoomKey: 'zoom_planning_theorique_vacs',
        Action: {
          OKey: `${VACS_OKEY_PREFIX},insert`,
          ...dto
        },
        useCache: false
      },
      false,
      true
    );
    return res;
  },

  async updatePlanningTheoriqueVac(item: Partial<PlanningTheoriqueVac>): Promise<any> {
    const dto = vacToDto(item);
    const res = await blobticService.executeZoomRequest<any>(
      {
        ZoomKey: 'zoom_planning_theorique_vacs',
        Action: {
          OKey: `${VACS_OKEY_PREFIX},update`,
          ...dto
        },
        useCache: false
      },
      false,
      true
    );
    return res;
  },

  async deletePlanningTheoriqueVac(id: number): Promise<any> {
    const res = await blobticService.executeZoomRequest<any>(
      {
        ZoomKey: 'zoom_planning_theorique_vacs',
        Action: {
          OKey: `${VACS_OKEY_PREFIX},delete`,
          THEOVAC_IDF: id
        },
        useCache: false
      },
      false,
      true
    );
    return res;
  },

  // ── Génération Planning ──
  async generatePlanning(params: PlanningGenerationParams): Promise<PlanningGenerationResult> {
    const res = await blobticService.executeZoomRequest<any>(
      {
        ZoomKey: 'zoom_planning_generation',
        Action: {
          OKey: `${GEN_OKEY_PREFIX},generate`,
          THEO_IDFS: params.theoIds.join(','),
          START_DATE: params.startDate,
          END_DATE: params.endDate
        },
        useCache: false
      },
      false,
      true
    );

    const rawData = Array.isArray(res.data) ? res.data[0] : (res.data || {});
    const generatedCount = Number(rawData?.generatedCount ?? rawData?.GENERATED_COUNT ?? 0);
    const skippedCount = Number(rawData?.skippedCount ?? rawData?.SKIPPED_COUNT ?? 0);
    const customMsg = (res.message && res.message !== 'ZoomExecutedSuccess') ? res.message : rawData?.message;

    return {
      generatedCount,
      skippedCount,
      message: customMsg || ''
    };
  },

  // ── Suppression Planning ──
  async getSessionsForDeletion(filters: PlanningDeletionFilters): Promise<SessionDeletionItem[]> {
    const action: Record<string, any> = {
      OKey: `${DEL_OKEY_PREFIX},select`,
      START_DATE: filters.startDate,
      END_DATE: filters.endDate
    };

    if (filters.roomIds && filters.roomIds.length > 0) {
      action.ROOM_IDFS = filters.roomIds.join(',');
    }
    if (filters.groupIds && filters.groupIds.length > 0) {
      action.GROUP_IDFS = filters.groupIds.join(',');
    }
    if (filters.teacherIds && filters.teacherIds.length > 0) {
      action.TEACHER_IDFS = filters.teacherIds.join(',');
    }

    const res = await blobticService.executeZoomRequest<any[]>(
      {
        ZoomKey: 'zoom_planning_deletion_select',
        Action: action as any,
        useCache: false
      },
      false,
      true
    );

    if (res.success && Array.isArray(res.data)) {
      return res.data.map(dto => ({
        id: Number(dto.SSN_IDF || dto.id || 0),
        uid: dto.SSN_UIDF || dto.uid,
        dos_idf: Number(dto.DOS_IDF || 1),
        arm_idf: Number(dto.ARM_IDF || 0),
        grp_idf: dto.GRP_IDF ? Number(dto.GRP_IDF) : undefined,
        tch_idf: dto.TCH_IDF ? Number(dto.TCH_IDF) : undefined,
        start_hour: dto.SSN_START_HOUR || dto.start_hour || '',
        end_hour: dto.SSN_END_HOUR || dto.end_hour || '',
        duration: dto.SSN_DURATION ? Number(dto.SSN_DURATION) : undefined,
        status: dto.SSN_STATUS || 'Scheduled',
        room_number: dto.ARM_ROOM_NUMBER || dto.room_number,
        room_lib: dto.ROOM_LIB || dto.room_lib,
        grp_name: dto.GRP_NAME || dto.grp_name,
        grp_code: dto.GRP_CODE || dto.grp_code,
        teacher_name: dto.TEACHER_NAME || dto.teacher_name
      }));
    }
    return [];
  },

  async deleteSessionsBatch(sessionIds: number[]): Promise<{ deletedCount: number; message: string }> {
    const res = await blobticService.executeZoomRequest<any>(
      {
        ZoomKey: 'zoom_planning_deletion_batch',
        Action: {
          OKey: `${DEL_OKEY_PREFIX},delete_batch`,
          SSN_IDFS: sessionIds.join(',')
        },
        useCache: false
      },
      false,
      true
    );

    const rawData = Array.isArray(res.data) ? res.data[0] : (res.data || {});
    const deletedCount = Number(rawData?.deletedCount ?? rawData?.DELETED_COUNT ?? sessionIds.length);
    const customMsg = (res.message && res.message !== 'ZoomExecutedSuccess') ? res.message : rawData?.message;

    return {
      deletedCount,
      message: customMsg || ''
    };
  }
};
